import { Component, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { IaFilesService } from 'src/app/services/ia-files.service';
import { UserService } from 'src/app/services/user.service';
import { AngularFirestore } from '@angular/fire/firestore';
import { AngularFireStorage } from '@angular/fire/storage';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';

import { set } from 'date-fns';

// Se asume que MathJax se carga en el index.html
declare global {
  interface Window {
    MathJax: any;
  }
}

@Component({
  selector: 'app-iafiles',
  templateUrl: './iafiles.component.html',
  styleUrls: ['./iafiles.component.css'],
})
export class IafilesComponent implements OnInit, OnDestroy {
  mainuserid!: string;
  mainuseremail: any;
  usuario: any;
  claseuid: string = '';

  // Gestión de sesiones de chat
  chatSessions: any[] = [];
  currentChatSessionId: string = '';
  groupedChatSessions: {
    today: any[];
    yesterday: any[];
    last7Days: any[];
    older: any[];
  } = {
    today: [],
    yesterday: [],
    last7Days: [],
    older: [],
  };
  mostrarDocumentos: boolean = true;
  mostrarEntregas: boolean = false;

  // Historial de conversación (acumulativo)
  conversationHistory: { role: string; content: string }[] = [
    {
      role: 'system',
      content:
        'Eres un asesor academico, que busca ayudar y aconsejar a profesores a preparar sus asignaturas.',
    },
  ];

  // Variables para el chat
  chatmessage: string = '';
  chatIa: any[] = []; // Mensajes obtenidos de Firestore
  loadingResponse: boolean = false;
  typingMessage: string = '';
  formattedTypingMessage: SafeHtml = '';
  entregas: any[] = [];

  loadEntregas(): void {
    this.firestore
      .collection('entregasAlumno', (ref) =>
        ref.where('clase', '==', this.claseuid).orderBy('fecha', 'desc')
      )
      .valueChanges({ idField: 'id' })
      .subscribe((data) => {
        this.entregas = data;
      });
  }

  // Documentos
  documentos: any[] = [];
  porcentaje: number | null = null;

  // Suscripciones
  private documentosSubscription!: Subscription;
  private chatMessagesSubscription!: Subscription;
  private chatSessionsSubscription!: Subscription;

  // Estado de la interfaz
  mostrarChat: boolean = false;

  // Variables opcionales para actualizar título, etc.
  private iteracionesChat: number = 0;

  constructor(
    private userService: UserService,
    private authenticationService: AuthenticationService,
    private iaFilesService: IaFilesService,
    private firestore: AngularFirestore,
    private angularfireStorage: AngularFireStorage,
    private activatedRoute: ActivatedRoute,
    private sanitizer: DomSanitizer
  ) {
    // Configura marked para que preserve saltos de línea en Markdown (útil para fórmulas multilinea)
    marked.setOptions({
      breaks: true,
    });
  }
  // --- Variables para bienvenida, sugerencias y recordatorio ---
  welcomeMessage: string = '👋 ¡Hola! Soy tu asistente académico. ¡Empecemos!';
  showWelcomePanel: boolean = false;
  quickSuggestions: string[] = [
    '¿Puedes sugerirme una secuencia didáctica basada en los materiales subidos?',
    'Genera una rúbrica de evaluación para esta asignatura.',
    'Sugiere adaptaciones del contenido para estudiantes con necesidades educativas especiales.',
    'Crea un examen tipo test con 10 preguntas sobre la asignatura.',
    '¿Qué actividades prácticas puedo incluir para reforzar el aprendizaje?',
    '¿Cómo puedo explicar este tema con un enfoque más visual o interactivo?',
    '¿Qué errores comunes cometen los estudiantes al abordar la asignatura y cómo los puedo prevenir?',
    'Ayúdame a convertir este contenido en una presentación clara para clase.',
    'Crea una lista de ejercicios con distintos niveles de dificultad sobre esta asignatura.',
    'Elabora preguntas tipo examen sobre la asignatura, con sus respuestas.	',
    '¿Qué actividad colaborativa puedo aplicar con este contenido?',
    'Diseña una presentación o esquema visual de la asignatura para proyectar en clase.',
  ];
  mostrarSugerenciaSubirDocs: boolean = false;
  interaccionesSinDocs: number = 0;

  asistentePerfil = {
    nombre: 'Asistente Académico',
    descripcion:
      'Especialista en diseño de cursos, exámenes y material educativo.',
  };
  // --- Fin de variables opcionales ---
  ngOnInit(): void {
    this.activatedRoute.params.subscribe((params) => {
      this.claseuid = params['claseuid'];

      // Cargar documentos
      this.loadDocuments();

      // ✅ Cargar entregas también
      this.loadEntregas();
    });

    // Cargar usuario y sesiones de chat
    this.authenticationService.getStatus().subscribe(
      (status) => {
        this.mainuserid = status.uid;
        this.mainuseremail = status.email;
        this.userService.getUserById(this.mainuserid).subscribe((user) => {
          if (user && user.length > 0) {
            this.usuario = user[0].payload.doc.data();

            // Aquí detectamos si es estudiante y cambiamos el mensaje
            if (this.usuario.rol === 'estudiante') {
              this.toggleView();
              this.quickSuggestions = [
                '¿Qué preguntas me podrían hacer en un examen sobre este tema?',
                '¿En qué debo enfocarme para repasar este contenido?',
                'Dame un esquema de estudio para prepararme sobre lo que hemos visto',
                '¿Me puedes decir de donde puedo sacar material de apoyo para la asignatura?',
                '¿Me puedes explicar este tema como si no supiese nada?',
                '¿Qué conceptos clave debo dominar en esta asignatura?',
                'Dame ideas para desarrollar una presentación basada en lo visto en clase.',
                'Hazme una lista de definiciones importantes de la asignatura.',
                'Proponme ejercicios con diferentes niveles de dificultad.',
              ];
              this.welcomeMessage =
                '🎓 ¡Hola! Soy tu asistente de estudio. Estoy aquí para ayudarte a entender tus clases.';
              this.asistentePerfil.descripcion =
                'Asistente de Estudio, especializado en ayudar a estudiantes a comprender conceptos y resolver dudas.';
            } else {
              this.welcomeMessage =
                '👋 ¡Hola! Soy tu asistente académico. ¡Empecemos!';
            }
          }
          this.loadChatSessions();
        });
      },
      (error) => console.error('Error al obtener autenticación:', error)
    );

    // Suscribirse a documentos
    this.documentosSubscription = this.firestore
      .collection('documentos', (ref) =>
        ref.where('clase', '==', this.claseuid)
      )
      .valueChanges({ idField: 'id' })
      .subscribe(
        (docs: any[]) => {
          this.documentos = docs;
        },
        (error) => console.error('Error al cargar documentos:', error)
      );
  }

  ngOnDestroy(): void {
    if (this.documentosSubscription) this.documentosSubscription.unsubscribe();
    if (this.chatMessagesSubscription)
      this.chatMessagesSubscription.unsubscribe();
    if (this.chatSessionsSubscription)
      this.chatSessionsSubscription.unsubscribe();
  }

  formatearFecha(fecha: Date): string {
    const dia = fecha.getDate().toString().padStart(2, '0');
    const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
    const año = fecha.getFullYear();
    return `${dia}/${mes}/${año}`;
  }

  // Convierte Markdown a HTML seguro
  parseMarkdown(content: string | undefined | null): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(marked(content || ''));
  }

  // -------------------------------------------------------------------
  // Métodos de Sesiones de Chat
  // -------------------------------------------------------------------

  loadChatSessions(): void {
    this.chatSessionsSubscription = this.firestore
      .collection('chatsFilesIa', (ref) =>
        ref
          .where('userId', '==', this.mainuserid)
          .where('clase', '==', this.claseuid) // Filtro adicional por clase
          .orderBy('createdAt', 'desc')
      )
      .snapshotChanges()
      .subscribe(
        (sessions: any[]) => {
          this.chatSessions = sessions.map((s) => ({
            id: s.payload.doc.id,
            ...s.payload.doc.data(),
          }));
          this.groupChatSessions();
          if (!this.currentChatSessionId && this.chatSessions.length > 0) {
            this.openChatSession(this.chatSessions[0]);
          }
        },
        (error) => console.error('Error al cargar sesiones de chat:', error)
      );
  }

  groupChatSessions(): void {
    const todayArr: any[] = [];
    const yesterdayArr: any[] = [];
    const last7DaysArr: any[] = [];
    const olderArr: any[] = [];

    this.chatSessions.forEach((session) => {
      const createdAt = session.createdAt
        ? new Date(session.createdAt.seconds * 1000)
        : new Date();
      if (this.isToday(createdAt)) {
        todayArr.push(session);
      } else if (this.isYesterday(createdAt)) {
        yesterdayArr.push(session);
      } else if (this.isWithinLast7Days(createdAt)) {
        last7DaysArr.push(session);
      } else {
        olderArr.push(session);
      }
    });

    this.groupedChatSessions = {
      today: todayArr,
      yesterday: yesterdayArr,
      last7Days: last7DaysArr,
      older: olderArr,
    };
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }

  isYesterday(date: Date): boolean {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return (
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear()
    );
  }

  isWithinLast7Days(date: Date): boolean {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return date >= sevenDaysAgo;
  }

  newChat(): void {
    const defaultTitle = 'Nuevo Chat ' + new Date().toLocaleDateString();
    const newSession = {
      userId: this.mainuserid,
      title: defaultTitle,
      createdAt: new Date(),
      clase: this.claseuid, // Se añade para filtrar por clase en el historial
    };
    this.firestore
      .collection('chatsFilesIa')
      .add(newSession)
      .then((docRef) => {
        newSession['id'] = docRef.id;
        this.chatSessions.unshift(newSession);
        this.groupChatSessions();
        this.currentChatSessionId = docRef.id;
        // Reinicia el historial para el nuevo chat
        this.conversationHistory = [
          {
            role: 'system',
            content:
              'Eres un asesor academico, que busca ayudar y aconsejar a profesores a preparar sus asignaturas.',
          },
        ];
        this.chatIa = [];
        this.subscribeToChatMessages();
      })
      .catch((error) => console.error('Error al crear nuevo chat:', error));
    this.showWelcomePanel = true;
    this.mostrarSugerenciaSubirDocs = false;
    this.interaccionesSinDocs = 0;
  }

  openChatSession(session: any): void {
    // Si se hace clic en una sesión diferente, se asigna la sesión actual
    if (this.currentChatSessionId !== session.id) {
      this.currentChatSessionId = session.id;
      // Aquí se puede optar por cargar el historial almacenado; en este ejemplo se inicia con un mensaje sistema
      if (this.conversationHistory.length === 0) {
        this.conversationHistory.push({
          role: 'system',
          content:
            'Eres un asesor academico, que busca ayudar y aconsejar a profesores a preparar sus asignaturas.',
        });
      }
    }
    this.subscribeToChatMessages();
    if (this.chatIa.length == 0) {
      this.showWelcomePanel = true;
    } else if (this.chatIa.length > 0) {
      this.showWelcomePanel = false;
    }
    this.mostrarSugerenciaSubirDocs = false;
    this.interaccionesSinDocs = 0;
  }

  subscribeToChatMessages(): void {
    if (this.chatMessagesSubscription)
      this.chatMessagesSubscription.unsubscribe();
    if (!this.currentChatSessionId) return;
    this.chatMessagesSubscription = this.iaFilesService
      .getMessages(this.currentChatSessionId)
      .subscribe(
        (messages: any[]) => {
          // Se procesa cada mensaje (aplicando correcciones matemáticas) al cargar
          this.chatIa = messages.map((msg) => {
            const data = msg.payload.doc.data();
            let rawText = data.message || '';
            let html = marked(rawText);
            html = this.fixMatrixNewlines(html);
            html = this.fixExponents(html);
            // Convierte delimitadores personalizados (por ejemplo, corchetes) a formato MathJax inline
            html = html.replace(
              /\[([\s\S]*?)\]/g,
              (_, formula) => `\\(${formula}\\)`
            );
            data.processedMessage = html;
            return { ...msg, processedMessage: data.processedMessage };
          });
          setTimeout(() => this.renderMathJax(), 0);
        },
        (error) => console.error('Error al cargar mensajes:', error)
      );
  }

  // -------------------------------------------------------------------
  // Métodos de Mensajes del Chat
  // -------------------------------------------------------------------

  sendMessage(): void {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    if (this.chatmessage.trim() !== '' && this.currentChatSessionId) {
      if (this.showWelcomePanel) {
        this.showWelcomePanel = false;
      }
      if (this.documentos.length === 0) {
        this.interaccionesSinDocs++;
        if (this.interaccionesSinDocs >= 3) {
          this.mostrarSugerenciaSubirDocs = true;
        }
      }
      const fecha = new Date();
      const mensajeUsuario = this.chatmessage;

      // Agregar mensaje del usuario al historial
      this.conversationHistory.push({ role: 'user', content: mensajeUsuario });

      // Generar prompt adaptado según el rol
      const systemPrompt = this.generarPromptSegunRol();
      const messagesToSend = [
        { role: 'system', content: systemPrompt },
        ...this.conversationHistory,
      ];

      // Guardar mensaje del usuario en Firestore
      const message = {
        sender: this.mainuserid,
        message: mensajeUsuario,
        fecha: fecha,
        nuevo: true,
      };

      this.iaFilesService
        .sendmessage(this.currentChatSessionId, message)
        .then(() => {
          this.loadingResponse = true;
          this.typingMessage = '';

          // Solicitar respuesta desde el backend
          this.iaFilesService.getResponse(messagesToSend).subscribe(
            (response) => {
              const respuesta = response.choices[0].message.content;
              this.typeTextEffect(respuesta, 0);
            },
            (error) => {
              console.error('Error en la respuesta de OpenAI:', error);
              this.loadingResponse = false;
            }
          );
        })
        .catch((error) => console.error('Error al enviar mensaje:', error));

      this.chatmessage = '';
      setTimeout(() => {
        this.isLoading = false;
      }, 10000);
    }
  }

  generarPromptSegunRol(): string {
    const contexto = this.construirContextoDocumentos();
    if (this.usuario?.rol === 'estudiante') {
      return `Eres un asistente educativo que apoya a estudiantes, pero bajo **ninguna circunstancia** debes dar respuestas, soluciones, resultados, procedimientos paso a paso ni realizar tareas por ellos.

Tu única función es **explicar conceptos** relacionados con el contenido proporcionado por el profesor y guiar al estudiante para que pueda resolver los problemas por sí mismo.

⚠️ No resuelvas preguntas.  
⚠️ No muestres resultados, respuestas numéricas ni fórmulas resueltas.  
⚠️ No digas "la respuesta es", "el resultado es", "entonces obtenemos", ni expresiones similares.  

En su lugar:
✅ Guía con preguntas.  
✅ Sugiere pasos generales sin aplicar operaciones.  
✅ Explica el significado de los conceptos involucrados.

Usa exclusivamente los documentos proporcionados para explicar.
Si alguna vez estás a punto de dar una respuesta, solución o número, detente. Reescribe tu respuesta solo como guía conceptual. No violes esta regla.
Contexto del profesor:
${contexto}`;
    } else {
      return `Eres un asesor académico, que busca ayudar y aconsejar a profesores a preparar sus asignaturas.
  Puedes generar actividades, exámenes y mejorar la estructura del curso con base en los documentos disponibles.\n\n${contexto}`;
    }
  }

  /**
   * Efecto "typewriter" para mostrar gradualmente la respuesta del asistente.
   * Al finalizar, corrige el contenido (matrices, exponentes) y agrega la respuesta al historial,
   * además de enviarla a Firestore.
   */
  isLoading: boolean = false;
  typeTextEffect(fullText: string, currentIndex: number): void {
    this.isLoading = true;
    if (currentIndex < fullText.length) {
      this.typingMessage += fullText[currentIndex];
      this.formattedTypingMessage = this.parseMarkdown(this.typingMessage);
      setTimeout(() => this.typeTextEffect(fullText, currentIndex + 1), 2);
    } else {
      // Una vez completado el efecto, se procesa el contenido para corregir fórmulas
      this.formattedTypingMessage = this.parseMarkdown(this.typingMessage);
      setTimeout(() => {
        this.processMathFormulas();
        // Agrega la respuesta del asistente al historial
        this.conversationHistory.push({
          role: 'assistant',
          content: this.typingMessage,
        });
        const messageRespuesta = {
          sender: 'openai',
          message: this.typingMessage,
          fecha: new Date(),
          nuevo: true,
        };
        // Envía el mensaje del asistente a Firestore
        this.iaFilesService
          .sendmessage(this.currentChatSessionId, messageRespuesta)
          .catch((error) =>
            console.error('Error al enviar respuesta de OpenAI:', error)
          );
        this.typingMessage = '';
        this.loadingResponse = false;

        // Incrementar contador de iteraciones (ej. cada vez que se finaliza un intercambio)
        this.iteracionesChat++;

        // Cuando se alcancen 4 iteraciones, genera un título usando OpenAI
      }, 0);
      this.isLoading = false;
      if (this.iteracionesChat >= 4) {
        this.generateChatTitleFromInitialMessages();
        this.iteracionesChat = 0;
      }
    }
  }
  generateChatTitleFromInitialMessages(): void {
    if (this.conversationHistory.length < 5) {
      console.warn('No hay suficientes mensajes para generar un título aún.');
      return;
    }

    // Solo tomamos las 4 primeras interacciones reales (ignoramos el primer system)
    const primerosMensajes = this.conversationHistory
      .filter((msg) => msg.role === 'user' || msg.role === 'assistant')
      .slice(0, 4)
      .map((msg) => {
        const autor = msg.role === 'user' ? 'Usuario' : 'Asistente';
        return `${autor}: ${msg.content}`;
      })
      .join('\n');

    const promptTitulo = `Basándote en las siguientes interacciones, genera un título breve de 3 a 4 palabras que resuma el tema principal:\n\n${primerosMensajes}`;

    // Llamamos a OpenAI
    this.iaFilesService
      .getResponse([
        {
          role: 'system',
          content:
            'Eres un generador de títulos breves para conversaciones educativas.',
        },
        { role: 'user', content: promptTitulo },
      ])
      .subscribe(
        (response) => {
          const tituloGenerado = response.choices[0].message.content.trim();
          console.log('Título generado automáticamente:', tituloGenerado);

          if (tituloGenerado && this.currentChatSessionId) {
            this.updateTitle(this.currentChatSessionId, tituloGenerado);
          }
        },
        (error) => {
          console.error(
            'Error al generar el título basado en las primeras interacciones:',
            error
          );
        }
      );
  }

  // Corrige saltos de línea en matrices: fuerza el uso de "\\" en bloques de matriz.
  fixMatrixNewlines(text: string): string {
    return text.replace(
      /(\\begin\{pmatrix\})([\s\S]*?)(\\end\{pmatrix\})/g,
      (match, begin, inner, end) => {
        const fixedInner = inner.replace(/\s*\\\s*/g, ' \\\\ ');
        return begin + fixedInner + end;
      }
    );
  }

  updateTitle(chatId: string, newTitle: string): Promise<void> {
    return this.firestore
      .collection('chatsFilesIa')
      .doc(chatId)
      .update({ title: newTitle })
      .then(() => {
        // Actualiza la sesión en la variable local
        const index = this.chatSessions.findIndex((chat) => chat.id === chatId);
        if (index !== -1) {
          this.chatSessions[index].title = newTitle;
          // Forzar cambio de referencia para que Angular detecte el cambio
          this.chatSessions = [...this.chatSessions];
          this.groupChatSessions();
        }
        console.log('Título actualizado correctamente a:', newTitle);
      })
      .catch((error) => {
        console.error('Error al actualizar el título:', error);
      });
  }

  // Corrige exponentes: convierte "n^3" en "n^{3}".
  fixExponents(text: string): string {
    return text.replace(/([a-zA-Z0-9])\^([^{}\s]+)/g, (match, base, exp) => {
      return `${base}^{${exp}}`;
    });
  }

  // Procesa el contenido matemático del contenedor "chat-container"
  processMathFormulas(): void {
    const chatContainer = document.getElementById('chat-container');
    if (chatContainer) {
      let html = chatContainer.innerHTML;
      html = this.fixMatrixNewlines(html);
      html = this.fixExponents(html);
      html = html.replace(
        /\[([\s\S]*?)\]/g,
        (_, formula) => `\\(${formula}\\)`
      );
      chatContainer.innerHTML = html;
    }
    this.renderMathJax();
  }
  deleteChat(chatId: string): void {
    if (
      confirm(
        '¿Estás seguro de que deseas borrar este chat? No se podrá recuperar.'
      )
    ) {
      // Elimina la sesión de chat de Firestore
      this.firestore
        .collection('chatsFilesIa')
        .doc(chatId)
        .delete()
        .then(() => {
          console.log('Chat eliminado correctamente');
          // Actualiza el arreglo de chatSessions eliminando el chat borrado
          this.chatSessions = this.chatSessions.filter(
            (chat) => chat.id !== chatId
          );
          // Re-agrupar las sesiones para actualizar la vista
          this.groupChatSessions();
          // Si el chat borrado es el actual, limpiar el estado del chat
          if (this.currentChatSessionId === chatId) {
            this.currentChatSessionId = '';
            // Reiniciar el historial y mensajes (ajustar según tu lógica de negocio)
            this.conversationHistory = [
              {
                role: 'system',
                content:
                  'Eres un asesor academico, que busca ayudar y aconsejar a profesores a preparar sus asignaturas.',
              },
            ];
            this.chatIa = [];
          }
        })
        .catch((error) => console.error('Error al borrar el chat:', error));
    }
  }

  renderMathJax(): void {
    if (window.MathJax && window.MathJax.typesetPromise) {
      window.MathJax.typesetPromise()
        .then(() => console.log('MathJax renderizado'))
        .catch((err: any) =>
          console.error('Error al renderizar MathJax:', err)
        );
    } else {
      console.warn('MathJax no está cargado aún.');
    }
  }

  construirPrompt(mensajeUsuario: string): string {
    const chatContexto = this.conversationHistory
      .map((msg) => {
        if (msg.role === 'user') return `Usuario: ${msg.content}`;
        if (msg.role === 'assistant') return `Asistente: ${msg.content}`;
        return `Sistema: ${msg.content}`;
      })
      .join('\n');
    const contextoDocumentos = this.construirContextoDocumentos();
    return `${contextoDocumentos}\n\n${chatContexto}`;
  }

  construirContextoDocumentos(): string {
    let contexto = 'Información de documentos:\n';
    this.documentos
      .filter((doc) => doc.activado) // <- Solo documentos activados
      .forEach((doc) => {
        contexto += `Documento: ${doc.nombre}\nResumen/Contenido: ${
          doc.contenido || 'Sin contenido disponible'
        }\n\n`;
      });
    return contexto;
  }

  // -------------------------------------------------------------------
  // Métodos para Documentos y Utilidades
  // -------------------------------------------------------------------

  loadDocuments(): void {
    // Los documentos se cargan en ngOnInit mediante la suscripción.
  }

  scrolltobottom(): void {
    const objDiv = document.getElementById('themessagesbox');
    if (objDiv) {
      objDiv.scrollTo({ top: objDiv.scrollHeight, behavior: 'smooth' });
    }
  }

  actualizarEstado(documento: any): void {
    console.log(
      `El estado de "${documento.nombre}" ahora es:`,
      documento.activado
    );
    if (documento.id) {
      this.firestore
        .collection('documentos')
        .doc(documento.id)
        .update({ activado: documento.activado })
        .catch((error) =>
          console.error('Error al actualizar el estado del documento:', error)
        );
    }
  }

  toggleView(): void {
    this.mostrarChat = !this.mostrarChat;
  }

  abrirDocumento(documento: any): void {
    const url = documento.localizacion || documento.documentoURL;
    if (url) {
      window.open(url, '_blank');
    } else {
      console.warn('No se encontró la URL del documento.');
    }
  }

  deleteDocumento(documento: any): void {
    if (documento && documento.localizacion && documento.id) {
      if (
        confirm(
          '¿Estás seguro de que deseas borrar este documento? No se podrá recuperar.'
        )
      ) {
        this.angularfireStorage.storage
          .refFromURL(documento.localizacion)
          .delete()
          .then(() => {
            this.firestore
              .collection('documentos')
              .doc(documento.id)
              .delete()
              .then(() => console.log('Documento borrado correctamente'))
              .catch((error) =>
                console.error('Error al borrar metadata:', error)
              );
          })
          .catch((error) =>
            console.error('Error al borrar archivo de Storage:', error)
          );
      }
    }
  }
  deleteEntrega(entrega: any): void {
    if (!entrega?.id) {
      console.warn('Entrega sin ID, no se puede borrar.');
      return;
    }

    if (confirm('¿Estás seguro de que deseas eliminar este trabajo?')) {
      // Borra el archivo en Storage (si existe)
      if (entrega.localizacion) {
        this.angularfireStorage.storage
          .refFromURL(entrega.localizacion)
          .delete()
          .catch((error) =>
            console.error('❌ Error al borrar archivo del Storage:', error)
          );
      }

      // Borra la metadata de Firestore
      this.firestore
        .collection('entregasAlumno')
        .doc(entrega.id)
        .delete()
        .then(() => {
          console.log('✅ Entrega eliminada correctamente');
          this.loadEntregas(); // Recargar lista
        })
        .catch((error) => {
          console.error('❌ Error al eliminar la entrega de Firestore:', error);
        });
    }
  }

  getColorIA(probabilidad: number): string {
    if (probabilidad >= 0.5) return 'red'; // Alta sospecha de IA
    if (probabilidad >= 0.25) return 'orange'; // Duda razonable
    return 'green'; // Baja sospecha
  }
  getColorHumana(probabilidad: number): string {
    if (probabilidad >= 0.7) return 'green'; // Alta redacción humana
    if (probabilidad >= 0.5) return 'orange'; // Dudoso
    return 'red'; // Baja redacción humana
  }

  uploadLocalFiles(event: any): void {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    const allowedExtensions = ['docx', 'txt', 'pptx', 'pdf'];

    Array.from(files).forEach((file) => {
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!extension || !allowedExtensions.includes(extension)) {
        alert(`El archivo "${file.name}" tiene una extensión no permitida.`);
        return;
      }

      const sanitizedFileName = file.name
        .replace(/\s/g, '')
        .replace(/[^a-zA-Z0-9-.]/g, '');
      const filePath = `${this.claseuid}/${Date.now()}_${sanitizedFileName}`;
      const fileRef = this.angularfireStorage.ref(filePath);
      const uploadTask = this.angularfireStorage.upload(filePath, file);

      uploadTask.percentageChanges().subscribe((porcentaje) => {
        if (porcentaje !== undefined && porcentaje !== null) {
          this.porcentaje = Math.round(porcentaje);
        }
      });

      uploadTask
        .then(() => {
          fileRef.getDownloadURL().subscribe((downloadURL) => {
            const documento = {
              nombre: file.name,
              localizacion: downloadURL,
              tipo: file.type,
              extension: extension,
              activado: true,
              provider: 'local',
              clase: this.claseuid,
              fechaSubida: new Date(),
              rutaInterna: filePath,
            };
            this.firestore
              .collection('documentos')
              .add(documento)
              .then((docRef) => {
                documento['id'] = docRef.id;
              })
              .catch((error) =>
                console.error('Error al guardar metadata en Firestore:', error)
              );
            this.porcentaje = null;
          });
        })
        .catch((error) => console.error('Error al subir el archivo:', error));
    });
  }
  construirContextoEntregas(): string {
    let contexto = 'Trabajos entregados por el alumno:\n';
    this.entregas.forEach((entrega) => {
      contexto += `Trabajo: ${entrega.nombre}\nContenido: ${
        entrega.contenido || 'Sin contenido disponible'
      }\n\n`;
    });
    return contexto;
  }

  uploadStudentWork(event: any): void {
    const file: File = event.target.files[0];
    if (!file) return;

    const extension = file.name.split('.').pop()?.toLowerCase();
    const allowedExtensions = ['docx', 'txt', 'pdf'];
    if (!extension || !allowedExtensions.includes(extension)) {
      alert('Solo se permiten archivos .docx, .txt o .pdf');
      return;
    }

    const sanitizedFileName = file.name
      .replace(/\s/g, '')
      .replace(/[^a-zA-Z0-9-.]/g, '');
    const filePath = `entregas/${
      this.claseuid
    }/${Date.now()}_${sanitizedFileName}`;
    const fileRef = this.angularfireStorage.ref(filePath);
    const uploadTask = this.angularfireStorage.upload(filePath, file);

    uploadTask.percentageChanges().subscribe((porcentaje) => {
      this.porcentaje = Math.round(porcentaje || 0);
    });

    uploadTask
      .then(() => {
        fileRef.getDownloadURL().subscribe((downloadURL) => {
          const entrega = {
            nombre: file.name,
            documentoURL: downloadURL,
            extension: extension,
            clase: this.claseuid,
            alumnoId: this.mainuserid,
            fecha: new Date(),
            rutaInterna: filePath,
            procesado: false,
          };

          this.firestore
            .collection('entregasAlumno')
            .add(entrega)
            .then((docRef) => {
              const entregaId = docRef.id;

              // Esperar procesamiento en la nube
              setTimeout(() => {
                this.firestore
                  .collection('entregasAlumno')
                  .doc(entregaId)
                  .get()
                  .subscribe((docSnap) => {
                    const docData = docSnap.data();
                    const texto =
                      (docData as { contenido?: string })?.contenido || '';

                    if (texto.trim().length === 0) {
                      console.warn('⚠️ Contenido aún no disponible o vacío.');
                      return;
                    }

                    const textoCortado = texto.trim().substring(0, 8000);
                    const messages = [
                      {
                        role: 'system',
                        content:
                          'Eres un sistema de detección que analiza textos para detectar plagio, uso de IA y redacción humana. Solo quiero un porcentaje de sospecha de IA, plagio y redacción humana. No quiero explicaciones ni detalles adicionales.Si detectas plagio no significa que no sea redaccion humana eso es compatible con la redacción humana, lo que no es compatible es la IA y la redaccion humana. Con esto digo que no es obligatorio que la suma de los porcentajes de el 100%',
                      },
                      {
                        role: 'user',
                        content: `Analiza el siguiente texto:\n\n${textoCortado}`,
                      },
                    ];

                    console.log('📤 Enviando a OpenAI:', messages);

                    this.iaFilesService.getResponse(messages).subscribe(
                      (response) => {
                        const resultText =
                          response?.choices?.[0]?.message?.content || '';
                        console.log('✅ Respuesta de OpenAI:', resultText);

                        const sospechaIA = this.extractScore(
                          resultText,
                          /IA:\s*(\d+)%?/i
                        );
                        const sospechaPlagio = this.extractScore(
                          resultText,
                          /plagio:\s*(\d+)%?/i
                        );

                        const redaccionHumana = this.extractScore(
                          resultText,
                          /redacción humana:\s*(\d+)%?/i
                        );

                        this.firestore
                          .collection('entregasAlumno')
                          .doc(entregaId)
                          .update({
                            sospechaIA: sospechaIA / 100,
                            sospechaPlagio: sospechaPlagio / 100,
                            redaccionHumana: redaccionHumana / 100,
                            procesado: true,
                          });

                        this.loadEntregas();
                      },
                      (err) => {
                        console.error(
                          '❌ Error al obtener respuesta de OpenAI:',
                          err
                        );
                      }
                    );
                  });
              }, 5000); // Espera por procesamiento de la Cloud Function
            });

          this.porcentaje = null;
        });
      })
      .catch((error) => {
        console.error('❌ Error al subir trabajo del alumno:', error);
      });
  }

  extractScore(text: string, regex: RegExp): number {
    const match = text.match(regex);
    return match ? parseInt(match[1], 10) : 0;
  }
  togglePanel(panel: 'documentos' | 'entregas') {
    this.mostrarDocumentos = panel === 'documentos';
    this.mostrarEntregas = panel === 'entregas';
  }

  getIconClass(documento: any): string {
    const ext = documento.extension?.toLowerCase() || '';
    switch (ext) {
      case 'pdf':
        return 'fa-regular fa-file-pdf';
      case 'docx':
        return 'fas fa-file-word';
      case 'pptx':
        return 'fas fa-file-powerpoint';
      case 'txt':
        return 'fas fa-file-alt';
      default:
        return 'fas fa-file';
    }
  }

  hasAnyChatSessions(): boolean {
    return (
      this.groupedChatSessions.today.length > 0 ||
      this.groupedChatSessions.yesterday.length > 0 ||
      this.groupedChatSessions.last7Days.length > 0 ||
      this.groupedChatSessions.older.length > 0
    );
  }
}
